const Promise = require('es6-promise');
const Logger = require('./utils/logger');
const assert = require('./utils/assert');
const assign = require('./utils/assign');
const snakeToCamel = require('./utils/snakeToCamel');

/**
 * Creates an instance of TokenManager.
 *
 * @constructor
 * @param {OAuth2Client} oauth2client Spotii's OAuth 2.0 client
 * @param {Storage} storage Initiated storage
 * @param {Object} [options] Options for initating Token Manager
 * @param {String} [options.storageKey='spotii:auth:token'] Key for storing token data in local storage.
 * @param {Number} [options.bufferTime=60000] Number of miliseconds before `access_token` expiration to refresh.. it is 1 minute
 * @param {Number} [options.saudiBufferTime=300000] Number of miliseconds before `access_token` expires.. it is 5 minutes..
 */
function TokenManager(oauth2client, storage, options) {
  const defaultOptions = {
    storageKey: 'spotii:auth:token',
    bufferTime: 60 * 60 * 1000, // ms
    saudiBufferTime: 10 * 60 * 1000, // ms
  };
  this.options = assign(defaultOptions, options);
  this.oauth2client = oauth2client;
  this.storage = storage;
  this.data = null;

  const oldData = this.storage.getItem(this.options.storageKey);
  if (oldData) {
    this.isSaudiUser = oldData.country === 'SAU';
  } else {
    this.isSaudiUser = false;
  }

  this.log = new Logger('[spotii-auth.token]');

  // Flag showing if token data was populated.
  this.isPopulated = false;
}

/**
 * Populate token data in memory and store it in the available storage.
 *
 * @method set
 * @param {Object} tokenData
 * @see TokenManager.parseToken()
 */
TokenManager.prototype.set = function(tokenData, isSaudiUser = false) {
  const data = TokenManager.parseToken(tokenData);
  const currTokenInfo = this.storage.getItem(this.options.storageKey) || {};
  if (!currTokenInfo.session_id_token) {
    data.session_id_token = data.id_token;
  } else {
    data.session_id_token = currTokenInfo.session_id_token;
  }
  this.isSaudiUser = isSaudiUser;
  this.data = data;
  this.storage.setItem(this.options.storageKey, this.data);
  this.isPopulated = true;
};

/**
 * Returns token object or provided property. Will return undefined if `key` is not exists or
 * data is not populated.
 *
 * @method get
 * @param {String} [key]
 * @returns {String|undefined}
 */
TokenManager.prototype.get = function(key) {
  const data = this.data;
  if (this.isPopulated) {
    if (!key) {
      return data;
    }
    if (key in data) {
      return data[key];
    }

    this.log.warn(`Key '${key}' is not valid token property.`);
    return undefined;
  }

  const msg = key
    ? `Trying to get '${key}' from not populated TokenManager.`
    : 'Trying to use token data of not populated TokenManager.';
  this.log.warn(msg);
  return undefined;
};

/**
 * Clear token data from memory and storage.
 *
 * @method flush
 */
TokenManager.prototype.flush = function() {
  const { storageKey } = this.options;
  this.storage.removeItem(storageKey);
  this.data = null;
  this.isPopulated = false;
};

/**
 * Check if `access_token` is expired taking in account buffer time set in options.
 *
 * @method expired
 * @returns {Boolean} True if `access_token` is expired
 */
TokenManager.prototype.expired = function() {
  return (
    this.get('expires_in') <
    Date.now() - (this.isSaudiUser ? this.options.saudiBufferTime : this.options.bufferTime)
  );
};

/**
 * Method return valid `access_token` if available and performing authomatic refresh if necessary.
 *
 * @method getToken
 * @returns {Promise} Valid `access_token`
 */
TokenManager.prototype.getToken = function(extraFetchParams = []) {
  const self = this;
  if (this.isPopulated) {
    // Refresh token before returning, if expired.
    if (this.expired()) {
      return this.refresh().then(function() {
        // Pass refreshed access_token to the next `.then`.
        const tokenInfo = {
          accessToken: self.get('access_token'),
        };
        extraFetchParams.map(param => {
          tokenInfo[snakeToCamel(param)] = self.get(param);
          return;
        });
        return tokenInfo;
      })
      .catch(function(err) {
        return Promise.reject(new Error(err));
      });
    }
    const tokenInfo = {
      accessToken: this.get('access_token'),
    };
    extraFetchParams.map(param => {
      tokenInfo[snakeToCamel(param)] = this.get(param);
      return;
    });
    return Promise.resolve(tokenInfo);
  }

  const msg = 'Trying to get an `access_token`, but there is no valid token available.';
  this.log.warn(msg);
  return Promise.reject(new Error(msg));
};

/**
 * Refresh `access_token` is available.
 *
 * @method refresh
 * @param {String} refreshToken
 * @returns {Promise} Refreshed token data
 */
TokenManager.prototype.refresh = function(refreshToken) {
  const self = this;

  if (this.isSaudiUser) {
    if (this.expired()) {
      return this.revoke().then(function() {
        const msg = 'EXPIRED_SESSION';
        self.log.warn(msg);
        self.flush();
        return Promise.reject(new Error(msg));
      });
    }

    const tokenData = this.storage.getItem(this.options.storageKey);
    // this.storage.setItem(this.options.storageKey, tokenData);
    self.set(tokenData, true);
    return Promise.resolve(tokenData);
  }

  const token = refreshToken || (this.isPopulated && this.get('refresh_token'));

  if (token && !this.isSaudiUser) {
    return this.oauth2client
      .token('refresh_token', token)
      .catch(function(e) {
        // Request fails, probably invalid `refresh_token`.
        self.flush();
        throw e;
      })
      .then(function(tokenData) {
        tokenData.expires_in = Date.now() + self.options.bufferTime;
        self.set(tokenData, false);
        return self.get();
      });
  }

  const msg = 'Trying to refresh `access_token`, but no valid refresh_token provided.';
  this.log.warn(msg);
  return Promise.reject(new Error(msg));
};

/**
 * Revoke token making it invalid for future usage. Revoking of `refresh_token` will also revoke
 * `access_token`.
 *
 * @method revoke
 * @returns {Promise}
 */
TokenManager.prototype.revoke = function() {
  const tokenType = 'refresh_token';
  const self = this;
  if (this.isPopulated) {
    return this.oauth2client.revokeToken(tokenType, this.get(tokenType)).then(function() {
      self.storage.getItem(self.options.storageKey);
      return Promise.resolve();
    });
  }

  const msg = `Trying to revoke \`${tokenType}\`, but token is not exists.`;
  this.log.warn(msg);
  return Promise.reject(new Error(msg));
};

/**
 * Try to restore token from the storage.
 *
 * @method restore
 * @returns {Promise} Refreshed access_token
 */
TokenManager.prototype.restore = async function() {
  var storedToken = {};

  // we need this condition since the storage implementation for react-native is async
  if (typeof document !== 'undefined') {
    // # web environment
    storedToken = this.storage.getItem(this.options.storageKey);
  } else if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    // # react-native environment
    storedToken = await this.storage.getItem(this.options.storageKey);
  }

  // eslint-disable-next-line dot-notation
  const refreshToken = storedToken && storedToken['refresh_token'];

  // Proactively refresh restored token
  if (refreshToken) {
    return this.refresh(refreshToken);
  }

  // We don't have valid refresh_token, so flush storage and reject
  this.flush();
  return Promise.reject();
};

/**
 * Method to assert and fetch neccessary token info.
 *
 * @static
 * @param {Object} data
 * @param {String} data.access_token
 * @param {String} data.refresh_token
 * @param {String} data.expires_in
 * @param {String} data.id_token
 * @param {String} data.user_id
 */
TokenManager.parseToken = function(data) {
  assert.check(
    data,
    { type: 'object', message: 'provided data must be an object' },
    {
      access_token: { type: 'string', message: 'access_token is required' },
      refresh_token: { type: 'string', message: 'refresh_token is required' },
      id_token: { type: 'string', message: 'id_token is required' },
      expires_in: { type: 'number', message: 'expires_in is required' },
    }
  );

  const reducer = function(obj, key) {
    // eslint-disable-next-line no-param-reassign
    // obj[key] = key === 'expires_in' ? Date.now() + Number(data[key]) : data[key];
    obj[key] = data[key];
    return obj;
  };
  const keys = ['access_token', 'refresh_token', 'expires_in', 'id_token', 'user_id'];
  const tokenData = keys.reduce(reducer, {});

  return tokenData;
};

module.exports = TokenManager;
